// src/pages/Landing.js

import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  AppBar,
  Toolbar,
  IconButton,
  Modal,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import {
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Google as GoogleIcon,
  PlayArrow as PlayIcon,
  Close as CloseIcon,
  Security as SecurityIcon,
  Shield as ShieldIcon,
  NightsStay as NightIcon,
  WbSunny as MorningIcon,
  Upload as ImportIcon,
  Extension as ExtensionIcon,
  Tab as TabIcon,
  Devices as DevicesIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import { useThemeMode } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import HeroDemo from '../components/HeroDemo';
import { APP_STORE_URL } from '../config/extensionUrls';
// TODO: Add feature flag context when available
// import { useFeatureFlags } from '../contexts/FeatureFlagProvider';

const Landing = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { darkMode, toggleDarkMode } = useThemeMode();
  const { loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const [demoOpen, setDemoOpen] = useState(false);

  // Detect user's browser
  const detectBrowser = () => {
    const userAgent = navigator.userAgent;
    if (userAgent.indexOf('Firefox') > -1) return 'firefox';
    if (userAgent.indexOf('Edg') > -1) return 'edge'; // Check Edge before Chrome
    if (userAgent.indexOf('Chrome') > -1) return 'chrome';
    if (userAgent.indexOf('Safari') > -1) return 'safari';
    return 'chrome'; // Default to Chrome
  };

  // react-snap creates the static page in Chromium. Defer browser-specific
  // copy until after hydration so every visitor starts from the same markup.
  const [userBrowser, setUserBrowser] = useState('chrome');

  useEffect(() => {
    setUserBrowser(detectBrowser());
  }, []);

  const handleGoogleSignIn = async () => {
    try {
      await loginWithGoogle();
      navigate('/sessions');
    } catch (error) {
      console.error('Sign in failed:', error);
    }
  };

  const openDemo = () => {
    setDemoOpen(true);
  };

  const closeDemo = () => {
    setDemoOpen(false);
  };

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Blackmount — Save your research session. Resume it anywhere.</title>
        <meta name="title" content="Blackmount — Save your research session. Resume it anywhere." />
        <meta
          name="description"
          content="Save a live set of browser tabs with context and reopen them later on another device or browser. One click to save, one click to resume. Free extension for Chrome, Firefox, Edge, Brave, Vivaldi, and Opera, plus an iOS app."
        />
        <meta name="keywords" content="save browser tabs, tab session manager, cross-device tab sync, resume browser session, browser tab manager, OneTab alternative, research session, save and restore tabs, Chrome tab extension, Firefox tab extension, Edge tab extension, Safari tab extension iOS, cross-browser session sync, browser extension, productivity tool" />
        <meta name="author" content="Blackmount" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://app.blackmount.ai/" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://app.blackmount.ai/" />
        <meta property="og:site_name" content="Blackmount" />
        <meta property="og:title" content="Blackmount — Save your research session. Resume it anywhere." />
        <meta
          property="og:description"
          content="Save a live set of browser tabs with context and reopen them later on another device or browser. One click to save, one click to resume."
        />
        <meta property="og:image" content="https://app.blackmount.ai/blackmount-logo.jpg" />
        <meta property="og:image:alt" content="Blackmount - Save your research session. Resume it anywhere." />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://app.blackmount.ai/" />
        <meta name="twitter:title" content="Blackmount — Save your research session. Resume it anywhere." />
        <meta
          name="twitter:description"
          content="Save a live set of browser tabs with context and reopen them later on another device or browser. One click to save, one click to resume."
        />
        <meta name="twitter:image" content="https://app.blackmount.ai/blackmount-logo.jpg" />
        <meta name="twitter:image:alt" content="Blackmount - Save your research session. Resume it anywhere." />

        {/* Additional SEO */}
        <meta name="application-name" content="Blackmount" />
        <meta name="apple-mobile-web-app-title" content="Blackmount" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="theme-color" content={darkMode ? "#121212" : "#ffffff"} />

        {/* Structured Data - JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Blackmount",
            "applicationCategory": "ProductivityApplication",
            "operatingSystem": "Web, Chrome, Firefox, Edge, Brave, Vivaldi, Opera, Safari (iOS), iOS",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "description": "Save your research session — all your browser tabs with context — and resume it later on another device or browser. One click to save, one click to resume.",
            "url": "https://app.blackmount.ai",
            "screenshot": "https://app.blackmount.ai/blackmount-logo.jpg",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "ratingCount": "127"
            }
          })}
        </script>

        {/* Performance */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="true" />
        <link rel="dns-prefetch" href="https://www.google-analytics.com" />
      </Helmet>

      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Minimal Top Bar */}
        <AppBar 
          position="static" 
          elevation={0}
          sx={{ 
            bgcolor: 'background.paper',
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Toolbar>
            <Box 
              sx={{ 
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Box
                component="img"
                src="/blackmount-logo.jpg"
                alt="Blackmount"
                sx={{
                  height: '32px',
                  width: 'auto',
                  borderRadius: 0.5
                }}
              />
            </Box>
            
            <Button
              variant="text"
              component={Link}
              to="/blog"
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 500,
                color: 'text.primary',
                display: { xs: 'none', sm: 'inline-flex' }
              }}
            >
              Blog
            </Button>

            <Button
              variant="text"
              component={Link}
              to="/pricing"
              sx={{
                mr: 1,
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 500,
                color: 'text.primary',
                display: { xs: 'none', sm: 'inline-flex' }
              }}
            >
              Pricing
            </Button>

            <IconButton
              color="inherit"
              onClick={toggleDarkMode}
              aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              sx={{ mr: 2 }}
            >
              {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>

            <Stack direction="row" spacing={1}>
              <Button
                variant="text"
                component={Link}
                to="/login"
                sx={{ 
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 500,
                  color: 'text.primary',
                }}
              >
                Sign in
              </Button>
              <Button 
                variant="outlined" 
                component={Link}
                to="/signup"
                sx={{ 
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Sign up
              </Button>
            </Stack>
          </Toolbar>
        </AppBar>

        {/* Hero Section */}
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Chip
              label="Free browser extension"
              color="success"
              variant="filled"
              sx={{ fontSize: '0.9rem', fontWeight: 500, mb: 3 }}
            />

            <Typography
              variant={isMobile ? "h3" : "h2"}
              component="h1"
              fontWeight={700}
              sx={{ mb: 3, color: 'text.primary' }}
              data-analytics="lp_hero_impression"
            >
              Save your research session.{' '}
              <Box component="span" sx={{ color: 'primary.main' }}>
                Resume it anywhere.
              </Box>
            </Typography>

            <Typography
              variant={isMobile ? "h6" : "h5"}
              color="text.secondary"
              sx={{ mb: 4, maxWidth: 700, mx: 'auto' }}
            >
              Save a live set of tabs with context and reopen them later. Sign in to sync across devices.
            </Typography>

            <Stack
              spacing={1.5}
              sx={{ maxWidth: 500, mx: 'auto', textAlign: 'left', mb: 5 }}
            >
              <Typography variant="body1" color="text.primary" sx={{ fontSize: '1.05rem' }}>
                <strong>Save all tabs in one click</strong> — your whole window, instantly
              </Typography>
              <Typography variant="body1" color="text.primary" sx={{ fontSize: '1.05rem' }}>
                <strong>Add a quick note</strong> — so future-you knows why
              </Typography>
              <Typography variant="body1" color="text.primary" sx={{ fontSize: '1.05rem' }}>
                <strong>Reopen the full session</strong> — on any supported browser or device
              </Typography>
            </Stack>

            {/* Primary CTAs - Browser Aware */}
            <Stack
              direction={isMobile ? "column" : "row"}
              spacing={2}
              justifyContent="center"
              sx={{ mb: 2 }}
            >
              {userBrowser === 'firefox' ? (
                <>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<ExtensionIcon />}
                    onClick={() => {
                      window.open('https://addons.mozilla.org/en-US/firefox/addon/blackmount/', '_blank', 'noopener,noreferrer');
                    }}
                    data-analytics="lp_cta_click"
                    data-button-id="extension-firefox"
                    sx={{
                      py: 1.5,
                      px: 4,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      borderRadius: 3,
                      textTransform: 'none',
                      bgcolor: '#FF7139',
                      '&:hover': {
                        bgcolor: '#E06329',
                      }
                    }}
                  >
                    Add to Firefox — Free
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      window.open('https://chromewebstore.google.com/detail/blackmount-%E2%80%93-save-restore/fpchhlhegjjjkfnhcdgfjcbinhhmfccc', '_blank', 'noopener,noreferrer');
                    }}
                    data-analytics="lp_cta_click"
                    data-button-id="extension-chrome-alt"
                    sx={{
                      borderRadius: 3,
                      textTransform: 'none',
                    }}
                  >
                    or get for Chrome
                  </Button>
                </>
              ) : userBrowser === 'edge' ? (
                <>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<ExtensionIcon />}
                    onClick={() => {
                      window.open('https://microsoftedge.microsoft.com/addons/detail/blackmount-%E2%80%93-save-resto/joehjcmaaehannjlhaaiikflfhcamdoh', '_blank', 'noopener,noreferrer');
                    }}
                    data-analytics="lp_cta_click"
                    data-button-id="extension-edge"
                    sx={{
                      py: 1.5,
                      px: 4,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      borderRadius: 3,
                      textTransform: 'none',
                      bgcolor: '#0078D4',
                      '&:hover': {
                        bgcolor: '#006ABC',
                      }
                    }}
                  >
                    Add to Edge — Free
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      window.open('https://chromewebstore.google.com/detail/blackmount-%E2%80%93-save-restore/fpchhlhegjjjkfnhcdgfjcbinhhmfccc', '_blank', 'noopener,noreferrer');
                    }}
                    data-analytics="lp_cta_click"
                    data-button-id="extension-chrome-alt"
                    sx={{
                      borderRadius: 3,
                      textTransform: 'none',
                    }}
                  >
                    or get for Chrome
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<ExtensionIcon />}
                    onClick={() => {
                      window.open('https://chromewebstore.google.com/detail/blackmount-%E2%80%93-save-restore/fpchhlhegjjjkfnhcdgfjcbinhhmfccc', '_blank', 'noopener,noreferrer');
                    }}
                    data-analytics="lp_cta_click"
                    data-button-id="extension-chrome"
                    sx={{
                      py: 1.5,
                      px: 4,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      borderRadius: 3,
                      textTransform: 'none',
                    }}
                  >
                    Add to Chrome — Free
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      window.open('https://addons.mozilla.org/en-US/firefox/addon/blackmount/', '_blank', 'noopener,noreferrer');
                    }}
                    data-analytics="lp_cta_click"
                    data-button-id="extension-firefox-alt"
                    sx={{
                      borderRadius: 3,
                      textTransform: 'none',
                    }}
                  >
                    or get for Firefox
                  </Button>
                </>
              )}

              <Box
                component="a"
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download Blackmount on the App Store"
                data-analytics="lp_cta_click"
                data-button-id="ios-appstore"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'scale(1.05)' },
                }}
              >
                <Box
                  component="img"
                  src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?releaseDate=1717372800"
                  alt="Download on the App Store"
                  sx={{ height: 56, width: 'auto', borderRadius: 1 }}
                />
              </Box>

              <Button
                variant="outlined"
                size="large"
                startIcon={<PlayIcon />}
                onClick={openDemo}
                data-analytics="lp_cta_click"
                data-button-id="demo"
                sx={{
                  py: 1.5,
                  px: 4,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 3,
                  textTransform: 'none',
                }}
              >
                See a 30-sec demo
              </Button>
            </Stack>

            {/* Google Sign-in + Signup Options */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                or
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center">
                <Button
                  variant="outlined"
                  startIcon={<GoogleIcon />}
                  onClick={handleGoogleSignIn}
                  data-analytics="lp_cta_click"
                  data-button-id="google"
                  sx={{
                    py: 1,
                    px: 3,
                    fontSize: '1rem',
                    fontWeight: 500,
                    borderRadius: 3,
                    textTransform: 'none',
                    color: 'text.primary',
                    borderColor: 'divider',
                  }}
                >
                  Continue with Google
                </Button>
                <Button
                  variant="outlined"
                  component={Link}
                  to="/signup"
                  data-analytics="lp_cta_click"
                  data-button-id="signup"
                  sx={{
                    py: 1,
                    px: 3,
                    fontSize: '1rem',
                    fontWeight: 500,
                    borderRadius: 3,
                    textTransform: 'none',
                  }}
                >
                  Sign up
                </Button>
              </Stack>
            </Box>

            {/* Privacy Note */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: '0.85rem' }}
            >
              Guest Mode works offline. Sign in to sync across devices.
            </Typography>

            {/* Browser Compatibility */}
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Works with your browser
              </Typography>
              <Stack
                direction="row"
                spacing={1.5}
                justifyContent="center"
                flexWrap="wrap"
                sx={{ gap: 1 }}
              >
                <Chip
                  label="Chrome"
                  variant="outlined"
                  size="small"
                  sx={{ fontWeight: 500, cursor: 'pointer' }}
                  onClick={() => window.open('https://chromewebstore.google.com/detail/blackmount-%E2%80%93-save-restore/fpchhlhegjjjkfnhcdgfjcbinhhmfccc', '_blank', 'noopener,noreferrer')}
                />
                <Chip
                  label="Firefox"
                  variant="outlined"
                  size="small"
                  sx={{ fontWeight: 500, cursor: 'pointer' }}
                  onClick={() => window.open('https://addons.mozilla.org/en-US/firefox/addon/blackmount/', '_blank', 'noopener,noreferrer')}
                />
                <Chip
                  label="Edge"
                  variant="outlined"
                  size="small"
                  sx={{ fontWeight: 500, cursor: 'pointer' }}
                  onClick={() => window.open('https://microsoftedge.microsoft.com/addons/detail/blackmount-%E2%80%93-save-resto/joehjcmaaehannjlhaaiikflfhcamdoh', '_blank', 'noopener,noreferrer')}
                />
                <Chip
                  label="Safari (iOS)"
                  variant="outlined"
                  size="small"
                  sx={{ fontWeight: 500, cursor: 'pointer' }}
                  onClick={() => window.open(APP_STORE_URL, '_blank', 'noopener,noreferrer')}
                />
                <Chip label="Brave" variant="outlined" size="small" sx={{ fontWeight: 500 }} />
                <Chip label="Vivaldi" variant="outlined" size="small" sx={{ fontWeight: 500 }} />
                <Chip label="Opera" variant="outlined" size="small" sx={{ fontWeight: 500 }} />
              </Stack>

              {/* Cross-browser sync callout — the wedge nobody else advertises */}
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Chip
                  label="Save in Safari on iPhone → open in Chrome on any device"
                  color="primary"
                  variant="filled"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    px: 0.5,
                    height: 'auto',
                    py: 0.75,
                    '& .MuiChip-label': { whiteSpace: 'normal', textAlign: 'center' },
                  }}
                />
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                <a
                  href="https://microsoftedge.microsoft.com/addons/detail/blackmount-%E2%80%93-save-resto/joehjcmaaehannjlhaaiikflfhcamdoh"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'inherit', textDecoration: 'none' }}
                >
                  Edge via Microsoft Edge Add-ons
                </a>
                {' • '}
                <a
                  href="https://chromewebstore.google.com/detail/blackmount-%E2%80%93-save-restore/fpchhlhegjjjkfnhcdgfjcbinhhmfccc"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'inherit', textDecoration: 'none' }}
                >
                  Chrome, Brave, Vivaldi, Opera via Chrome Web Store
                </a>
                {' • '}
                <a
                  href="https://addons.mozilla.org/en-US/firefox/addon/blackmount/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'inherit', textDecoration: 'none' }}
                >
                  Firefox via Mozilla Add-ons
                </a>
              </Typography>
            </Box>
          </Box>

          {/* Animated demo: save → sync → resume across devices */}
          <HeroDemo />

          {/* How It Works - Below the fold */}
          <Box sx={{ textAlign: 'center', py: 6, mb: 6 }}>
            <Typography variant="h5" component="h2" fontWeight={700} gutterBottom sx={{ mb: 4 }}>
              How it works
            </Typography>
            <Grid container spacing={4} justifyContent="center">
              <Grid item xs={12} md={4}>
                <Typography variant="h2" component="div" aria-hidden="true" sx={{ mb: 1 }}>1</Typography>
                <Typography variant="h6" component="h3" fontWeight={600} gutterBottom>Capture</Typography>
                <Typography variant="body1" color="text.secondary">
                  Click the Blackmount icon. Your entire window — every tab — is saved in one click.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="h2" component="div" aria-hidden="true" sx={{ mb: 1 }}>2</Typography>
                <Typography variant="h6" component="h3" fontWeight={600} gutterBottom>Annotate</Typography>
                <Typography variant="body1" color="text.secondary">
                  Add a quick note so future-you remembers the context. Close your tabs and breathe.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="h2" component="div" aria-hidden="true" sx={{ mb: 1 }}>3</Typography>
                <Typography variant="h6" component="h3" fontWeight={600} gutterBottom>Resume</Typography>
                <Typography variant="body1" color="text.secondary">
                  Open Blackmount on any of your devices. Click "Resume" and your tabs reopen in the order you saved them.
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* Why Switch Section */}
          <Box sx={{
            textAlign: 'center',
            py: 6,
            mb: 6,
            bgcolor: 'background.paper',
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            px: 4
          }}>
            <Typography variant="h5" component="h2" fontWeight={700} gutterBottom sx={{ mb: 1 }}>
              Tired of losing tabs when you switch browsers?
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Local-only tab managers trap your data in one browser. Blackmount sets it free.
            </Typography>

            <Grid container spacing={3} justifyContent="center">
              <Grid item xs={12} md={5}>
                <Box sx={(t) => ({
                  p: 3,
                  bgcolor: alpha(t.palette.error.main, 0.06),
                  border: `1px solid ${alpha(t.palette.error.main, 0.3)}`,
                  color: 'text.primary',
                  borderRadius: 2
                })}>
                  <Typography variant="subtitle1" component="h3" fontWeight={600} color="error.main" sx={{ mb: 2 }}>
                    Local-only tab managers
                  </Typography>
                  <Stack spacing={1}>
                    <Typography variant="body2">• Data stuck in one browser</Typography>
                    <Typography variant="body2">• Lost if browser crashes</Typography>
                    <Typography variant="body2">• Can't access from phone/laptop</Typography>
                    <Typography variant="body2">• Little organization or search</Typography>
                  </Stack>
                </Box>
              </Grid>
              <Grid item xs={12} md={5}>
                <Box sx={(t) => ({
                  p: 3,
                  bgcolor: alpha(t.palette.success.main, 0.08),
                  border: `1px solid ${alpha(t.palette.success.main, 0.4)}`,
                  color: 'text.primary',
                  borderRadius: 2
                })}>
                  <Typography variant="subtitle1" component="h3" fontWeight={600} color="success.main" sx={{ mb: 2 }}>
                    Blackmount
                  </Typography>
                  <Stack spacing={1}>
                    <Typography variant="body2">• Works offline like OneTab</Typography>
                    <Typography variant="body2">• Syncs across all your devices</Typography>
                    <Typography variant="body2">• Access from 6 browsers + iPhone</Typography>
                    <Typography variant="body2">• Search, organize, add notes</Typography>
                  </Stack>
                </Box>
              </Grid>
            </Grid>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
              Start in Guest Mode — no account needed. Sign in later to unlock sync.
            </Typography>
          </Box>

          {/* Use Cases Strip */}
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              flexWrap="wrap"
              sx={{ gap: 2 }}
            >
              <Chip label="Job search" variant="outlined" sx={{ fontSize: '0.9rem', fontWeight: 500 }} />
              <Chip label="Research papers" variant="outlined" sx={{ fontSize: '0.9rem', fontWeight: 500 }} />
              <Chip label="Project switching" variant="outlined" sx={{ fontSize: '0.9rem', fontWeight: 500 }} />
              <Chip label="Travel planning" variant="outlined" sx={{ fontSize: '0.9rem', fontWeight: 500 }} />
              <Chip label="Online shopping" variant="outlined" sx={{ fontSize: '0.9rem', fontWeight: 500 }} />
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 2 }}
            >
              Save 30 tabs of research. Come back tomorrow. Pick up exactly where you left off.
            </Typography>
          </Box>

          {/* Value Cards */}
          <Grid container spacing={4} sx={{ mb: 8 }}>
            <Grid item xs={12} md={4}>
              <Card 
                elevation={0}
                sx={{ 
                  height: '100%',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 3,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                  }
                }}
              >
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Box sx={{ mb: 3 }}>
                    <TabIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                  </Box>
                  <Typography variant="h6" component="h3" fontWeight={600} sx={{ mb: 2 }}>
                    One-click session save
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Capture a whole window and restore the full tab set later, in order.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card 
                elevation={0}
                sx={{ 
                  height: '100%',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 3,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                  }
                }}
              >
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Box sx={{ mb: 3 }}>
                    <DevicesIcon sx={{ fontSize: 48, color: 'secondary.main' }} />
                  </Box>
                  <Typography variant="h6" component="h3" fontWeight={600} sx={{ mb: 2 }}>
                    Sync across devices
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Save on your laptop, resume on your phone — or any device. One account, every screen.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card 
                elevation={0}
                sx={{ 
                  height: '100%',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 3,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                  }
                }}
              >
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Box sx={{ mb: 3 }}>
                    <PublicIcon sx={{ fontSize: 48, color: 'success.main' }} />
                  </Box>
                  <Typography variant="h6" component="h3" fontWeight={600} sx={{ mb: 2 }}>
                    Works across 6 browsers
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Chrome, Firefox, and Edge today. Switch browsers without losing your tabs.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Old "How it Works" removed — replaced by the new 3-step section above the fold */}

          {/* Trust Row */}
          <Box sx={{ 
            py: 6, 
            px: 4,
            bgcolor: 'background.paper',
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            textAlign: 'center',
          }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Private by default. No background tracking. Secure cloud storage.
            </Typography>

            <Grid container spacing={4} justifyContent="center">
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <GoogleIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Google Sign-In
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Secure authentication
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <SecurityIcon sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Secure storage
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Enterprise-grade infrastructure
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <ShieldIcon sx={{ fontSize: 32, color: 'secondary.main', mb: 1 }} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Privacy first
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    No tracking
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Coming Soon Badge - Hidden for production launch */}
          {/* TODO: Re-enable with feature flag when ready to advertise upcoming features */}
        </Container>

        {/* Footer */}
        <Box sx={{ 
          py: 4, 
          px: 4,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper',
        }}>
          <Container maxWidth="lg">
            <Stack
              direction={isMobile ? "column" : "row"}
              spacing={3}
              justifyContent="center"
              alignItems="center"
            >
              <Button color="inherit" size="small" href="/privacy-policy">Privacy</Button>
              <Button color="inherit" size="small" href="/terms">Terms of Service</Button>
              <Button color="inherit" size="small">Security</Button>
              <Button color="inherit" size="small" href="mailto:support@blackmount.ai">Contact</Button>
              <Button color="inherit" size="small" href="https://x.com/Blackmount_ai" target="_blank" rel="noopener noreferrer">X / Twitter</Button>
              <Button color="inherit" size="small" href="https://www.linkedin.com/company/blackmount-ai/" target="_blank" rel="noopener noreferrer">LinkedIn</Button>
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', textAlign: 'center', mt: 3 }}
            >
              &copy; 2025-2026 Blackmount. All rights reserved.
            </Typography>
          </Container>
        </Box>
      </Box>

      {/* Demo Modal */}
      <Modal
        open={demoOpen}
        onClose={closeDemo}
        aria-labelledby="demo-modal-title"
        aria-describedby="demo-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 600 },
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: 24,
          p: 4,
          maxHeight: '80vh',
          overflow: 'auto',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography id="demo-modal-title" variant="h6" component="h2" fontWeight={600}>
              Blackmount in 30 seconds
            </Typography>
            <IconButton onClick={closeDemo} size="small" aria-label="Close demo">
              <CloseIcon />
            </IconButton>
          </Box>

          <Stack spacing={3}>
            <Box sx={{
              p: 3,
              bgcolor: 'background.default',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`
            }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Save
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Researching laptops for school? Click "Save Session" and all your tabs
                are captured instantly.
              </Typography>
            </Box>

            <Box sx={{
              p: 3,
              bgcolor: 'background.default',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`
            }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Add context
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add a quick note: "Best options under $1000" —
                so tomorrow you remember exactly what you were thinking.
              </Typography>
            </Box>

            <Box sx={{
              p: 3,
              bgcolor: 'background.default',
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`
            }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Restore
              </Typography>
              <Typography variant="body2" color="text.secondary">
                When you're ready, restore the session exactly where you left off—
                all tabs reopen in a new window.
              </Typography>
            </Box>

            <Stack spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="contained"
                fullWidth
                size="large"
                startIcon={<ExtensionIcon />}
                onClick={() => {
                  closeDemo();
                  window.open('https://chromewebstore.google.com/detail/blackmount-%E2%80%93-save-restore/fpchhlhegjjjkfnhcdgfjcbinhhmfccc', '_blank', 'noopener,noreferrer');
                }}
                data-analytics="landing-install"
                sx={{ borderRadius: 2 }}
              >
                Install Extension — No signup needed
              </Button>
              <Button
                variant="outlined"
                fullWidth
                size="large"
                startIcon={<GoogleIcon />}
                onClick={() => {
                  closeDemo();
                  handleGoogleSignIn();
                }}
                data-analytics="landing-signin"
                sx={{ borderRadius: 2 }}
              >
                Or sign in to sync across devices
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>
    </>
  );
};

export default Landing;
